import React from 'react';
import { useAIAssistant } from '../AIAssistantProvider';

const LiveChatPanel: React.FC = () => {
  const ctx = useAIAssistant();
  const { theme, liveChats, selectedChat, chatMessages, chatInput, chatSending, chatEndRef } = ctx;

  if (!selectedChat) {
    // Conversation List
    return (
      <div className="p-4 flex-1 hover-scroll overflow-y-auto min-h-0">
        {liveChats.length === 0 ? (
          <div className="text-center py-8">
            <svg xmlns="http://www.w3.org/2000/svg" className={`w-8 h-8 mx-auto mb-3 ${theme.isDark ? 'text-slate-600' : 'text-slate-300'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
            </svg>
            <p className={`text-[11px] font-medium ${theme.isDark ? 'text-slate-400' : 'text-slate-500'}`}>No active visitor chats</p>
          </div>
        ) : (
          <div className="space-y-2">
            {liveChats.map(chat => (
              <div
                key={chat.id}
                onClick={() => { ctx.setSelectedChat(chat.id); ctx.fetchChatMessages(chat.id); }}
                className={`p-3 rounded-xl border cursor-pointer transition-all ${
                  chat.status === 'pending_human'
                    ? theme.isDark ? 'bg-red-950/20 border-red-800/40 hover:bg-red-950/30' : 'bg-red-50 border-red-200 hover:bg-red-100'
                    : chat.status === 'assigned'
                    ? theme.isDark ? 'bg-green-950/20 border-green-800/40 hover:bg-green-950/30' : 'bg-green-50 border-green-200 hover:bg-green-100'
                    : theme.isDark ? 'bg-slate-800 border-slate-700 hover:bg-slate-700' : 'bg-white border-slate-200 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${
                      chat.status === 'pending_human' ? 'bg-red-500 animate-pulse' :
                      chat.status === 'assigned' ? 'bg-green-500' : 'bg-blue-500'
                    }`} />
                    <span className={`text-[11px] font-bold ${theme.isDark ? 'text-white' : 'text-slate-900'}`}>
                      {chat.visitor_name || 'Visitor'}
                    </span>
                  </div>
                  <span className={`text-[9px] ${theme.isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                    {new Date(chat.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <p className={`text-[10px] truncate ${theme.isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  {chat.last_message || 'New conversation'}
                </p>
                <div className="flex items-center justify-between mt-1.5">
                  <span className={`text-[8px] font-bold uppercase px-1.5 py-0.5 rounded-full ${
                    chat.status === 'pending_human' ? 'bg-red-100 text-red-700' :
                    chat.status === 'assigned' ? 'bg-green-100 text-green-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {chat.status === 'pending_human' ? 'Waiting' : chat.status === 'assigned' ? 'Active' : chat.status}
                  </span>
                  {chat.status === 'pending_human' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); ctx.acceptLiveChat(chat.id); }}
                      className="text-[9px] font-bold text-green-600 hover:text-green-700 px-2 py-0.5 rounded-lg hover:bg-green-50 transition-colors"
                    >
                      Accept
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const currentChat = liveChats.find(c => c.id === selectedChat);

  // Chat View
  return (
    <>
      {/* Chat header */}
      <div className={`p-3 border-b shrink-0 ${theme.isDark ? 'border-slate-700' : 'border-slate-200'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={() => { ctx.setSelectedChat(null); ctx.setChatMessages([]); }}
              className={`p-1 rounded-lg transition-colors ${theme.isDark ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
            <div>
              <p className={`text-[11px] font-bold ${theme.isDark ? 'text-white' : 'text-slate-900'}`}>
                {currentChat?.visitor_name || 'Visitor'}
              </p>
              <p className={`text-[9px] ${theme.isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                {currentChat?.status === 'assigned' ? 'Connected' : 'Waiting'}
              </p>
            </div>
          </div>
          <div className="flex gap-1">
            {currentChat?.status === 'pending_human' && (
              <button onClick={() => ctx.acceptLiveChat(selectedChat)} className="text-[9px] font-bold px-2 py-1 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors">Accept</button>
            )}
            {currentChat?.status === 'assigned' && (
              <button onClick={() => ctx.resolveLiveChat(selectedChat)} className={`text-[9px] font-bold px-2 py-1 rounded-lg transition-colors ${theme.isDark ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}>Resolve</button>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2 hover-scroll min-h-0">
        {chatMessages.map(msg => (
          <div key={msg.id} className={`flex ${msg.sender === 'agent' ? 'justify-end' : msg.sender === 'system' ? 'justify-center' : 'justify-start'}`}>
            {msg.sender === 'system' ? (
              <span className={`text-[9px] italic py-1 px-2 rounded-full ${theme.isDark ? 'text-slate-500 bg-slate-800' : 'text-slate-400 bg-slate-100'}`}>{msg.content}</span>
            ) : (
              <div className={`max-w-[80%] rounded-2xl px-3 py-2 ${
                msg.sender === 'agent' ? 'bg-blue-600 text-white' :
                msg.sender === 'ai' ? (theme.isDark ? 'bg-slate-800 text-slate-200' : 'bg-slate-100 text-slate-800') :
                theme.isDark ? 'bg-slate-700 text-white' : 'bg-slate-200 text-slate-900'
              }`}>
                <p className={`text-[8px] font-bold mb-0.5 ${msg.sender === 'agent' ? 'text-blue-200' : theme.isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                  {msg.sender === 'visitor' ? 'Visitor' : msg.sender === 'ai' ? 'AI' : 'You'}
                </p>
                <p className="text-[11px] whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                <p className={`text-[8px] mt-0.5 ${msg.sender === 'agent' ? 'text-blue-300' : theme.isDark ? 'text-slate-600' : 'text-slate-400'}`}>
                  {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            )}
          </div>
        ))}
        <div ref={chatEndRef} />
      </div>

      {/* Input (only when assigned) */}
      {currentChat?.status === 'assigned' && (
        <div className={`p-3 border-t shrink-0 ${theme.isDark ? 'border-slate-700' : 'border-slate-200'}`}>
          <div className="flex gap-2">
            <input
              type="text"
              value={chatInput}
              onChange={e => ctx.setChatInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && ctx.sendChatMessage()}
              placeholder="Type reply..."
              disabled={chatSending}
              className={`flex-1 px-3 py-2 rounded-xl text-[11px] border focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                theme.isDark ? 'bg-slate-800 border-slate-700 text-white placeholder-slate-500' : 'bg-white border-slate-200 text-slate-900'
              }`}
            />
            <button
              onClick={ctx.sendChatMessage}
              disabled={!chatInput.trim() || chatSending}
              className="px-3 py-2 rounded-xl bg-blue-600 text-white text-[11px] font-bold hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              Send
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default LiveChatPanel;
